/**
 * The subtitle keys (1.9.0), one map for the asset Subtitles tab and the sequence editor's Subtitles side tab: active
 * while that tab is open. Every key is matched on `e.code` (as Player.tsx and Editor.tsx do), so layouts and the Mac's
 * Option dead keys do not change them.
 *
 * Stream B: check each against the Player's handler, editCommands.ts and the slip keys, and by hand inside the cue
 * text area in Safari and Chrome on the Finnish layout (⌥N and ⌥I are dead keys on the US Mac layout, ⌥↑/↓ move the
 * caret); change a key here where it clashes, and list them in Help, the editor's Keys tab and the ? window
 * (SUBTITLE_KEY_GROUP).
 */
import type { EditorKeyGroup } from '../pages/create/editorKeys';

export type SubtitleKeyAction =
  | { action: 'start-at-playhead' }
  | { action: 'end-at-playhead' }
  | { action: 'new-cue' }
  | { action: 'split' }
  | { action: 'merge-next' }
  /** The active edge (the last set with ⌥I/⌥O or clicked) by frames: ±1, or ±10 with ⇧. */
  | { action: 'nudge'; frames: number }
  | { action: 'play-cue' }
  | { action: 'previous-cue' }
  | { action: 'next-cue' }
  /** In a cue's text area: wrap the selection in <i> / <b>. */
  | { action: 'italic' }
  | { action: 'bold' };

export interface SubtitleKeyDef {
  code: string;
  alt?: boolean;
  /** ⌘ on a Mac, Ctrl elsewhere. */
  mod?: boolean;
  shift?: boolean;
  result: SubtitleKeyAction;
}

export const SUBTITLE_KEYS: readonly SubtitleKeyDef[] = [
  { code: 'KeyI', alt: true, result: { action: 'start-at-playhead' } },
  { code: 'KeyO', alt: true, result: { action: 'end-at-playhead' } },
  { code: 'KeyN', alt: true, result: { action: 'new-cue' } },
  { code: 'KeyS', alt: true, result: { action: 'split' } },
  { code: 'KeyM', alt: true, result: { action: 'merge-next' } },
  { code: 'Comma', alt: true, result: { action: 'nudge', frames: -1 } },
  { code: 'Period', alt: true, result: { action: 'nudge', frames: 1 } },
  { code: 'Comma', alt: true, shift: true, result: { action: 'nudge', frames: -10 } },
  { code: 'Period', alt: true, shift: true, result: { action: 'nudge', frames: 10 } },
  { code: 'KeyP', alt: true, result: { action: 'play-cue' } },
  { code: 'ArrowUp', alt: true, result: { action: 'previous-cue' } },
  { code: 'ArrowDown', alt: true, result: { action: 'next-cue' } },
  { code: 'KeyI', mod: true, result: { action: 'italic' } },
  { code: 'KeyB', mod: true, result: { action: 'bold' } },
];

/** ⌘ (a Mac) or Ctrl (elsewhere) for the text keys. */
export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** The subtitle action for a key press, or null. `isMac` picks ⌘ (else Ctrl) for the text keys. */
export function subtitleKeyAction(e: Pick<KeyboardEvent, 'code' | 'altKey' | 'shiftKey' | 'metaKey' | 'ctrlKey'>, isMac: boolean): SubtitleKeyAction | null {
  const mod = isMac ? e.metaKey : e.ctrlKey;
  const other = isMac ? e.ctrlKey : e.metaKey;
  if (other) return null;
  const hit = SUBTITLE_KEYS.find((k) => k.code === e.code && Boolean(k.alt) === e.altKey && Boolean(k.mod) === mod && Boolean(k.shift) === e.shiftKey);
  return hit ? hit.result : null;
}

/** The keys as Help, the editor's Keys tab and the ? window list them. */
export const SUBTITLE_KEY_GROUP: EditorKeyGroup = {
  area: 'Subtitles (while the Subtitles tab is open)',
  rows: [
    { label: 'Set the selected cue’s start / end to the playhead', keys: ['⌥I', '⌥O'], main: true },
    { label: 'New cue at the playhead (3 s)', keys: ['⌥N'], main: true },
    { label: 'Split the cue at the playhead and the text caret / merge it with the next', keys: ['⌥S', '⌥M'] },
    { label: 'Nudge the active edge a frame / ten frames', keys: ['⌥,', '⌥.', '⇧⌥,', '⇧⌥.'] },
    { label: 'Play the selected cue', keys: ['⌥P'] },
    { label: 'Previous / next cue', keys: ['⌥↑', '⌥↓'] },
    { label: 'Italic / bold on the selected words', keys: ['⌘I', '⌘B'] },
  ],
};
